/**
 * App-wide private-room watch (installed once by <App>, so nothing is missed while the lobby is unmounted):
 *
 * - `roomSettings` keeps the host's rules ("2 DRIVES · SPECIALS ON") for the current room. The lobby used to hold
 *   them in component state filled only by a live `room_state`, so a host coming back from Results saw the room
 *   without its rules line (QA r3).
 * - A player who disappears from the room gets a toast ("AMIR LEFT THE ROOM · YOU'RE THE HOST NOW"): the guest
 *   used to be silently promoted to host.
 */
import { signal } from '@preact/signals-core';
import type { RoomSettings } from '@hf/shared';
import type { OnlineClient } from '../net/types';
import type { UiStore } from './store';

export const roomSettings = signal<{ code: string; settings: RoomSettings } | null>(null);

/** Settings of the room with this code (null for another room / none yet). */
export const settingsForRoom = (code: string | null | undefined): RoomSettings | null => {
  const r = roomSettings.value;
  return code && r && r.code === code ? r.settings : null;
};

export const installRoomWatch = (online: OnlineClient, ui: UiStore): (() => void) => {
  let last: { code: string; hostId: string; players: { id: string; name: string }[] } | null = null;
  const subs: (() => void)[] = [];
  try {
    subs.push(
      online.on('room_state', (m) => {
        roomSettings.value = { code: m.code, settings: m.settings };
        const me = ui.net.peek().playerId;
        // only while the room is on screen: mid-match / on Results the net banner and the rematch line say it
        if (last && last.code === m.code && ui.screen.peek() === 'online_lobby') {
          for (const p of last.players) {
            if (p.id === me || m.players.some((q) => q.id === p.id)) continue;
            const promoted = me !== undefined && m.hostId === me && last.hostId !== me;
            const host = promoted ? " · YOU'RE THE HOST NOW" : '';
            ui.toast(`${(p.name || 'YOUR FRIEND').toUpperCase()} LEFT THE ROOM${host}`, 'info', 3500);
          }
        }
        last = {
          code: m.code,
          hostId: m.hostId,
          players: m.players.map((p) => ({ id: p.id, name: p.name })),
        };
      }),
      online.on('room_closed', () => {
        roomSettings.value = null;
        last = null;
      }),
    );
  } catch {
    /* online optional (tests, offline builds) */
  }
  return () => {
    for (const u of subs) u();
  };
};
